import React, { useEffect } from 'react'
import { useUrlParams } from '../hooks/useUrlParams'
import { auth } from '../firebaseConfig'
import { Box, CircularProgress } from '@mui/material'
import { AuthenticatedLayout } from '../components/App/AuthenticatedLayout'

import { useFirebaseTokenListener } from '../hooks/useFirebaseTokenListener'
import { useUserInit } from '../hooks/useUserInit'
import { useFeatureFlagsInit } from '../hooks/useFeatureFlags'
import { useOrgInit } from '../hooks/useOrgInit'
import { useTemplatesInit } from '../hooks/useTemplatesInit'
import { useGroupsInit } from '../hooks/useGroupsInit'
import { useAdapterInit } from '../hooks/useAdapterInit'
import UserAgreement from '../components/App/UserAgreement'
import Login from './Login'
import AccountDisabled from './AccountDisabled'
import { SessionState, useAppStore } from '../store/appStore'
import { assertNever } from '../utils/assertNever'
import { userOf } from '../store/sessionActions'
import { isReadOnly } from '../models/UserModel'
import { showMessage } from '../utils/snackbar'
import PrivacyPolicy from '../components/App/PrivacyPolicy'
import UsageInstructions from '../components/App/UsageInstructions'
import { useIntercom } from '../hooks/useIntercom'
import { useSentryUser } from '../hooks/useSentyUser'
import { tokenRefresher } from '../services/firebase/tokenRefresher'
import { useSentryBreadcrumbsButtonName } from '../hooks/useSentryBreadcrumbsButtonName'
import { useGenericAndroidKDetection } from '../hooks/useGenericAndroidKDetection'
import { SESSION_STORAGE_SHOW_UPDATE_NOTIFICATION_KEY, useVersionCheck } from '../hooks/useVersionCheck'
import { useEditingTracker } from '../hooks/useEditingTracker'
import { useTranslation } from 'react-i18next'

const centeredSpinner = (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
    </Box>
)

/** What to render for a session tag that gates the whole app, or null to keep going ('active',
 * and 'accountDisabled' exempted as read-only, don't gate anything on their own — see
 * SessionState). An exhaustive switch, not an if/else chain: adding a tag without extending this
 * fails `tsc` here instead of silently falling through to the "no user yet" fallback below. */
const renderSessionGate = (session: SessionState, onLogout: () => void): React.ReactElement | null => {
    switch (session.tag) {
        // Before Firebase's onIdTokenChanged has reported anything: neither confirmed signed-out
        // nor confirmed restored, so Login must not render for what might still turn out to be a
        // restored session (MD-1633).
        case 'initializing':
            return centeredSpinner
        case 'unauthenticated':
        case 'accountNotFound':
            return <Login />
        case 'resolving':
            // Stay on Login for an interactive sign-in so nothing flickers; a restored session gets a spinner.
            if (session.cameFrom === 'login') return <Login />
            return centeredSpinner
        case 'accountDisabled':
            // A deactivated account can't accept anything — except a read-only one, exempt so
            // it keeps seeing that page instead (MD-1497).
            return isReadOnly(session.user) ? null : <AccountDisabled onLogout={onLogout} />
        case 'active':
            return null
        default:
            return assertNever(session)
    }
}

const App: React.FC = () => {
    const { t } = useTranslation()
    const session = useAppStore((state) => state.session)

    useEffect(() => {
        const shouldShowNotification = sessionStorage.getItem(SESSION_STORAGE_SHOW_UPDATE_NOTIFICATION_KEY)
        if (shouldShowNotification) {
            showMessage(t('common.app_updated'), 'info', 15_000)
            sessionStorage.removeItem(SESSION_STORAGE_SHOW_UPDATE_NOTIFICATION_KEY) // Clean up the flag
        }
    }, [t])

    useEffect(() => {
        console.info('[App] Initializing token refresher')
        tokenRefresher.start()
    }, [])

    // Guards the same invariant as the `if (!user)` fallback below (see its own comment) — split
    // out as its own effect because showMessage is a side effect (it mutates snackbarStore and
    // plays a sound) and so can't run directly in the render body. Mirrors renderSessionGate's
    // own tag check rather than calling it, since that needs handleLogout, defined further down.
    useEffect(() => {
        const isPastSessionGate =
            session.tag === 'active' || (session.tag === 'accountDisabled' && isReadOnly(session.user))
        if (isPastSessionGate && !userOf(session)) {
            console.error('[App] Reached the agreement gates with no user.')
            showMessage(t('errors.user_info'), 'error')
        }
    }, [session, t])

    useFirebaseTokenListener()
    useUrlParams() // Fetch params from URL
    useUserInit() // Fetch user
    useFeatureFlagsInit()
    useOrgInit() // Fetch organization
    useTemplatesInit() // Fetch templates and favorites
    useGroupsInit() // Fetch user's groups
    useAdapterInit() // Fetch integration status
    useIntercom()
    useSentryUser()
    useSentryBreadcrumbsButtonName()
    useGenericAndroidKDetection()
    useVersionCheck() // Check version for updates
    useEditingTracker() // Track focus on editable fields (appStore.isEditing)

    const handleLogout = async () => {
        try {
            await auth.signOut()
            console.info('[App] User logged out successfully.')
        } catch (error) {
            console.error('[App] Logout failed:', error)
            showMessage(t('errors.logout'), 'error')
        }
    }

    const sessionGate = renderSessionGate(session, handleLogout)
    if (sessionGate) return sessionGate

    const user = userOf(session)
    if (!user) {
        // Unreachable given the current SessionState construction sites (isReadOnly of a
        // null user is always false, so the accountDisabled check above already returned) —
        // kept as a safe fallback rather than an assertion. Logging and the user-visible
        // message live in the effect above, not here: this is the render body, not an effect
        // or event handler.
        return <Login />
    }
    if (user.accepted_agreement === false) return <UserAgreement />
    if (user.accepted_privacy_policy === false) return <PrivacyPolicy />
    if (user.accepted_usage_instructions === false) return <UsageInstructions />

    return <AuthenticatedLayout onLogout={handleLogout} />
}

export default App
